import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import {
  MatDrawer,
  MatDrawerContainer,
  MatDrawerContent,
} from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { truncate } from 'lodash-es';
import { catchError, tap, throwError } from 'rxjs';
import {
  HasPermissionDirective,
  PermissionService,
  WebSocketService,
} from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../components/dialog/dialog.service';
import { ClientStateService } from '../clients/services/client-state.service';
import { ClientsService } from '../clients/services/clients.service';

@Component({
  selector: 'app-client-workspace',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatDrawer,
    MatDrawerContent,
    MatDrawerContainer,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    HasPermissionDirective,
  ],
  templateUrl: './client-workspace.component.html',
  styleUrl: './client-workspace.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientWorkspaceComponent implements OnDestroy {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _clientService = inject(ClientsService);
  private readonly _clientStateService = inject(ClientStateService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _dialog = inject(DialogService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _wsService = inject(WebSocketService);

  private readonly _clientId = this._route.snapshot.params['clientId'];
  private readonly _currentClient = this._clientStateService.currentClient;

  protected readonly clientInfo = computed(() => ({
    name: this._currentClient()?.name,
    idAlias: this._currentClient()?.idAlias,
  }));

  private readonly _allMenuItems: {
    label: string;
    icon: string;
    route: string;
    permission?: string | string[];
  }[] = [
    { label: 'Overzicht', icon: 'dashboard', route: 'overview' },
    { label: 'Apparaten', icon: 'memory', route: 'devices', permission: 'device:read' },
    {
      label: 'Interacties',
      icon: 'record_voice_over',
      route: 'interactions',
      permission: 'interaction:read',
    },
    { label: 'Historie', icon: 'history', route: 'history' },
    {
      label: 'Planning',
      icon: 'today',
      route: 'scheduler',
      permission: 'client_event:read',
    },
    {
      label: 'Signalering',
      icon: 'leak_add',
      route: 'signaling',
      permission: 'scm:read',
    },
    {
      label: 'Media',
      icon: 'subscriptions',
      route: 'media',
      permission: 'media:read',
    },
    {
      label: 'Instellingen',
      icon: 'settings',
      route: 'settings',
      permission: 'client:update',
    },
  ];

  protected readonly menuItems = computed(() =>
    this._allMenuItems.filter((item) => {
      if (!item.permission) return true;
      return Array.isArray(item.permission)
        ? this._permissionService.hasAnyPermission(item.permission)
        : this._permissionService.hasPermission(item.permission);
    })
  );

  constructor() {
    this._clientService
      .getCachedClient(this._clientId)
      .pipe(tap((result) => this._clientStateService.setState(result)))
      .subscribe();

    this._listenForMoveStarted();
  }

  ngOnDestroy() {
    this._clientStateService.clearState();
  }

  navigateTo(subroute: string): void {
    this._router.navigate([subroute], { relativeTo: this._route });
  }

  moveClient(): void {
    this._router.navigate(['clients', this._clientId, 'move']);
  }

  deleteClient(): void {
    const client = this._currentClient();
    if (!client) return;

    this._dialog.confirm({
      title: `${client.name} verwijderen?`,
      message:
        'De cliënt wordt gepland voor verwijdering en kan binnen 10 dagen hersteld worden. ' +
        'Weet je zeker dat je de cliënt wilt verwijderen?',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () =>
        this._clientService.delete(client._id).pipe(
          tap(() => {
            this._snackbar.open(
              `Cliënt ${truncate(client.name)} is gepland voor verwijdering`,
              'OK'
            );
            this._router.navigate(['/clients']);
          }),
          catchError((error) => {
            this._dialog.alert({
              title: 'Foutmelding',
              message: `Er is iets misgegaan bij het verwijderen.`,
              confirmText: 'OK',
            });
            return throwError(() => error);
          })
        ),
    });
  }

  /**
   * Listen for a move-started event on the current client via WebSocket.
   * When received, close all dialogs and redirect to the client list.
   *
   * This provides instant notification when another user initiates a
   * move for this client, even if the current user is idle (no HTTP
   * requests to trigger the interceptor safety net).
   */
  private _listenForMoveStarted(): void {
    this._wsService
      .connect<{ clientId: string }>('/event-stream', {
        requireAuth: true,
        topics: [`client/${this._clientId}/move-started`],
      })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (connection) => {
          connection.events$.subscribe((event) => {
            if (event.eventType === 'client.move.started') {
              this._matDialog.closeAll();
              this._snackbar.open(
                'Deze cliënt wordt verplaatst en kan niet bewerkt worden.',
                'OK'
              );
              this._router.navigate(['/clients']);
            }
          });
        },
      });
  }
}

